// M3 offshore incidents (M3_SPEC §4, §6, §14): helicopter crashes, storm damage, capsize, abandonment, mooring failure,
// shallow gas, diving. Deaths and injuries go through crew.injureCrew so the memorial, compensation and reputation
// rules of M1/M2 apply unchanged.
import { nextFloat, int } from '../../core/rng.js';
import { eqDef, stat, fieldDef } from '../defs.js';
import { injureCrew, activeRigCrew } from '../crew.js';
import { notify } from '../util.js';
import { hasUpgrade } from '../research.js';

export const M3_INCIDENT_NAMES = {
  helicopter_crash: 'Helicopter crash', storm_damage: 'Storm damage', capsize: 'Capsize', abandonment: 'Abandonment',
  mooring_failure: 'Mooring failure', shallow_gas: 'Shallow gas blowout', diving: 'Diving accident', platform_fire: 'Platform fire',
};

// Share of an incident type a rig's safety gear (and offshore upgrades) removes, 0..0.95.
export function m3Mitigation(state, rig, type) {
  let keep = 1;
  for (const id of rig?.safety?.upgrades ?? []) keep *= 1 - (stat(eqDef(id), 'mitigates', {})[type] ?? 0);
  if (hasUpgrade(state, 'survival_training') && (type === 'abandonment' || type === 'capsize')) keep *= 0.7;
  return Math.min(0.95, 1 - keep);
}

// Kill `deaths` and injure `injured` of the given crew (random picks), then notify one accident event.
export function m3Accident(state, type, { rig = null, well = null, fieldId = null, crew = null, deaths = 0, injured = 0, text = '', pause = true, extra = {} } = {}) {
  const pool = [...(crew ?? (rig ? activeRigCrew(state, rig) : []))];
  const cause = M3_INCIDENT_NAMES[type] ?? type;
  const fates = [];
  let dead = 0;
  for (let i = 0; i < deaths && pool.length; i++) {
    const c = pool.splice(Math.floor(nextFloat(state) * pool.length), 1)[0];
    if (injureCrew(state, c, cause.toLowerCase(), { cause, fatalChance: 1, wellId: well?.id ?? null, accident: type, silent: true }) === 'dead') { dead++; fates.push(`${c.name} was killed`); }
  }
  for (let i = 0; i < injured && pool.length; i++) {
    const c = pool.splice(Math.floor(nextFloat(state) * pool.length), 1)[0];
    injureCrew(state, c, cause.toLowerCase(), { cause, fatalChance: 0, days: [7, 60], wellId: well?.id ?? null, accident: type, silent: true });
    fates.push(`${c.name} was injured`);
  }
  const fid = fieldId ?? well?.fieldId ?? null;
  state.company.stats.incidents = (state.company.stats.incidents ?? 0) + 1;
  if (state.safety) state.safety.daysWithoutIncident = 0;
  if (rig?.safety) rig.safety.daysWithoutIncident = 0;
  const where = rig?.name ?? fieldDef(fid)?.name ?? '';
  notify(state, 'accident', { incidentType: type, wellId: well?.id ?? null, fieldId: fid, rigUid: rig?.uid ?? null, pause, fatal: dead > 0, deaths: dead, offshore: true,
    text: `${cause}${where ? ` — ${where}` : ''}. ${text}${fates.length ? ` ${fates.join('; ')}.` : ''}`.trim(), level: 'warn', ...extra });
  (state.accidents ??= []).push({ type, day: state.day, wellId: well?.id ?? null, rigId: rig?.uid ?? null, fieldId: fid, deaths: dead });
  if (state.accidents.length > 40) state.accidents.splice(0, state.accidents.length - 40);
  return dead;
}

export const pickInt = (state, lo, hi) => int(state, lo, hi);
